import { useState, type ComponentType } from 'react'
import { Link, NavLink, useNavigate } from 'react-router'
import { HouseIcon, LayoutListIcon, MenuIcon, PlusIcon, UserRoundIcon } from 'lucide-react'
import { toast } from 'sonner'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useAuth } from '@/features/auth/AuthProvider'
import { isModerator, useAccount } from '@/features/auth/account'
import { signInPath, useCurrentPath } from '@/features/auth/guards'
import { cn } from '@/lib/utils'
import { SECONDARY_NAV, useTabBarVisible } from './navigation'

// Navegação inferior do celular, como em um aplicativo: as quatro áreas principais ao
// alcance do polegar e, no centro, a ação de propor em destaque. No desktop, o cabeçalho
// faz esse papel. Vidro fosco só aqui (e no cabeçalho), com fundo quase opaco.

const ITEM =
  'group flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-xl text-[0.6875rem] font-medium text-muted-foreground transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring'

/** Ícone do item: no item ativo, fica "afundado" numa pílula. */
function TabIcon({ icon: Icon, active }: { icon: ComponentType<{ className?: string }>; active: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-8 w-14 items-center justify-center rounded-full transition-[background-color,box-shadow] duration-200',
        active ? 'bg-subtle text-primary shadow-pressed' : 'group-hover:text-foreground',
      )}
    >
      <Icon className="size-[1.375rem]" />
    </span>
  )
}

function Tab({ to, label, icon, end }: { to: string; label: string; icon: ComponentType<{ className?: string }>; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => cn(ITEM, isActive && 'font-semibold text-primary')}>
      {({ isActive }) => (
        <>
          <TabIcon icon={icon} active={isActive} />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  )
}

export function BottomNav() {
  const visible = useTabBarVisible()
  const { user } = useAuth()
  const current = useCurrentPath()
  const [moreOpen, setMoreOpen] = useState(false)

  if (!visible) return null

  return (
    <nav
      aria-label="Navegação principal"
      className="glass-chrome pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-white/70 shadow-[0_-8px_24px_-12px_rgb(30_44_70/0.18)] md:hidden"
    >
      <div className="mx-auto grid h-[var(--tabbar-height)] max-w-md grid-cols-5 items-stretch px-1.5 py-1.5">
        <Tab to="/" end label="Início" icon={HouseIcon} />
        <Tab to="/propostas" label="Propostas" icon={LayoutListIcon} />
        <Link to="/propor" className={cn(ITEM, 'text-foreground')}>
          <span
            aria-hidden="true"
            className="-mt-5 flex size-12 items-center justify-center rounded-full border-4 border-background bg-primary text-primary-foreground shadow-cta transition-transform active:scale-95"
          >
            <PlusIcon className="size-6" strokeWidth={2.5} />
          </span>
          <span className="truncate">Propor</span>
        </Link>
        {user ? (
          <Tab to="/conta" label="Conta" icon={UserRoundIcon} />
        ) : (
          <Link to={signInPath(current)} className={ITEM}>
            <TabIcon icon={UserRoundIcon} active={false} />
            <span className="truncate">Entrar</span>
          </Link>
        )}
        <button type="button" className={ITEM} onClick={() => setMoreOpen(true)} aria-haspopup="dialog">
          <TabIcon icon={MenuIcon} active={moreOpen} />
          <span className="truncate">Mais</span>
        </button>
      </div>
      <MoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
    </nav>
  )
}

/** Menu "Mais": páginas secundárias, painel da equipe e saída da conta. */
function MoreSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { user, signOut } = useAuth()
  const { data: account } = useAccount()
  const navigate = useNavigate()
  const close = () => onOpenChange(false)

  const handleSignOut = async () => {
    close()
    await signOut()
    toast.success('Você saiu da sua conta.')
    void navigate('/')
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto rounded-t-3xl pb-safe">
        <SheetHeader className="pb-1">
          <SheetTitle>Mais</SheetTitle>
          <SheetDescription className="sr-only">Outras páginas do Poder Aberto</SheetDescription>
        </SheetHeader>
        <div className="space-y-5 px-4 pb-6">
          {SECONDARY_NAV.map((group) => (
            <nav key={group.title} aria-label={group.title} className="space-y-1">
              <h2 className="px-3 font-mono text-[0.6875rem] font-medium tracking-[0.12em] text-muted-foreground uppercase">
                {group.title}
              </h2>
              <ul>
                {group.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      onClick={close}
                      className="flex min-h-11 items-center rounded-xl px-3 text-[0.9375rem] font-medium hover:bg-subtle"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
          {user ? (
            <div className="space-y-1 border-t border-border pt-4">
              {isModerator(account) ? (
                <Link
                  to="/admin"
                  onClick={close}
                  className="flex min-h-11 items-center rounded-xl px-3 text-[0.9375rem] font-medium hover:bg-subtle"
                >
                  Painel da equipe
                </Link>
              ) : null}
              <button
                type="button"
                onClick={() => void handleSignOut()}
                className="flex min-h-11 w-full items-center rounded-xl px-3 text-left text-[0.9375rem] font-medium text-muted-foreground hover:bg-subtle hover:text-foreground"
              >
                Sair da conta
              </button>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  )
}
